import { Account } from '@mls-wallet/shared-types';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router';
import { StyledTableCell, StyledTableRow } from './ui/styled-table';
import { useAccountUsdDetails } from '@/hooks/account/useAccountUsdDetails';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { AccountDisplay } from './accounts/account-display';
import { NetworkBadges } from './shared/network-badges';
import { cn, formatUsdValue } from '@/lib/utils';

interface AccountRowProps
  extends ComponentPropsWithoutRef<typeof StyledTableRow> {
  account: Account;
  leadingCell?: ReactNode;
  actionCell?: ReactNode;
}

export const AccountRow = forwardRef<HTMLTableRowElement, AccountRowProps>(
  ({ account, leadingCell, actionCell, className, ...props }, ref) => {
    const navigate = useNavigate();
    const { accountUsdDetails, isLoading } = useAccountUsdDetails(account.id);
    const totalUsdValue = isLoading
      ? null
      : accountUsdDetails
        ? Number(accountUsdDetails.totalUsdValue)
        : null;

    const handleRowClick = (accountId: string) => {
      navigate(`/accounts/${accountId}`);
    };

    return (
      <StyledTableRow
        ref={ref}
        onClick={() => handleRowClick(account.id)}
        className={cn('cursor-pointer', className)}
        {...props}
      >
        {leadingCell ? (
          <StyledTableCell className="w-10 px-1 first:pl-1 py-2 text-center">
            {leadingCell}
          </StyledTableCell>
        ) : null}
        <StyledTableCell className={cn(leadingCell && 'pl-0')}>
          <AccountDisplay
            account={account}
            className="gap-1"
            accountNameClassName="text-base font-semibold"
          />
        </StyledTableCell>
        <StyledTableCell className="w-[160px] whitespace-nowrap tabular-nums">
          <div>
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
            ) : (
              formatUsdValue(totalUsdValue)
            )}
          </div>
        </StyledTableCell>
        <StyledTableCell>
          <NetworkBadges networks={account.networks} maxVisible={3} />
        </StyledTableCell>
        {actionCell ? (
          <StyledTableCell className="w-12 text-center">
            {actionCell}
          </StyledTableCell>
        ) : null}
      </StyledTableRow>
    );
  }
);

AccountRow.displayName = 'AccountRow';
